import { useEffect, useState, useRef } from "react"
import { supabase } from "../../lib/supabase"
import { HomeIcon, VehicleIcon, PaymentsIcon, VideoIcon, CameraIcon, EyeIcon, ChatIcon, PhoneCallIcon, EmailIcon } from "../../lib/cccIcons"
import { useAuth } from "../../contexts/AuthContext"
import useIsMobile from "../../lib/useIsMobile"
import toast from "react-hot-toast"

const BODY_TYPES = ["sedan","suv","hatchback","pickup","van","crossover","coupe","convertible","minivan","wagon"]
const TRANSMISSIONS = ["automatic","manual","cvt","dct"]
const FUEL_TYPES = ["petrol","diesel","hybrid","electric","lpg"]
const DRIVE_TYPES = ["2wd","4wd","awd"]

const EMPTY = {
  brand:"", model:"", variant:"", year:new Date().getFullYear(), body_type:"sedan",
  engine_cc:"", transmission:"automatic", fuel_type:"petrol", drive_type:"2wd",
  color:"", interior_color:"", seats:5, doors:4,
  price:"", discount_price:"", price_negotiable:false,
  description:"", features:"", photos:[], video_url:"",
  showroom_name:"", showroom_location:"", showroom_phone:"", showroom_email:"",
  stock_count:1
}

export default function MyNewCarListings() {
  const { user, profile } = useAuth()
  const isMobile = useIsMobile()
  const [listings, setListings] = useState([])
  const [enquiries, setEnquiries] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState("listings")
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [videoUploadProgress, setVideoUploadProgress] = useState(0)
  const [fees, setFees] = useState({ listing_fee:2000, duration_days:30, lead_fee:500, featured_day:100, featured_week:500, featured_month:1500 })
  const [isDealer, setIsDealer] = useState(false)
  const [dealerStatus, setDealerStatus] = useState(null)
  const [showDealerForm, setShowDealerForm] = useState(false)
  const [dealerForm, setDealerForm] = useState({ showroom_name:"", showroom_location:"", showroom_phone:"", showroom_email:"", brands_sold:"", monthly_stock:"" })
  const [submittingDealer, setSubmittingDealer] = useState(false)
  const photoRef = useRef(null)

  useEffect(() => {
    if (!user) return
    load()
    // Load dealer status
    supabase.from("profiles").select("is_dealer").eq("id", user.id).single()
      .then(({data}) => setIsDealer(data?.is_dealer||false))
    supabase.from("dealer_applications").select("status").eq("user_id", user.id).maybeSingle()
      .then(({data}) => setDealerStatus(data?.status||null))
    supabase.from("app_settings").select("key,value").in("key",["new_car_listing_fee","new_car_listing_duration_days","new_car_lead_fee","new_car_featured_fee_day","new_car_featured_fee_week","new_car_featured_fee_month"])
      .then(({ data }) => {
        const map = {}
        data?.forEach(r => { map[r.key] = Number(r.value) })
        setFees({ listing_fee: map.new_car_listing_fee||2000, duration_days: map.new_car_listing_duration_days||30, lead_fee: map.new_car_lead_fee||500, featured_day: map.new_car_featured_fee_day||100, featured_week: map.new_car_featured_fee_week||500, featured_month: map.new_car_featured_fee_month||1500 })
      })
  }, [user])

  async function load() {
    const [{ data: lst }, { data: enq }] = await Promise.all([
      supabase.from("new_car_listings").select("*").eq("dealer_id", user.id).order("created_at",{ascending:false}),
      supabase.from("car_enquiries_view").select("*, new_car_listings(brand,model,year)").eq("dealer_id", user.id).order("created_at",{ascending:false})
    ])
    setListings(lst||[])
    setEnquiries(enq||[])
    setLoading(false)
  }

  async function uploadPhoto(file) {
    setUploadingPhoto(true)
    try {
      const ext = file.name.split(".").pop()
      const path = `new-cars/${user.id}/${Date.now()}.${ext}`
      const { error } = await supabase.storage.from("marketplace").upload(path, file, { upsert:true })
      if (error) throw error
      const { data } = supabase.storage.from("marketplace").getPublicUrl(path)
      setForm(f=>({...f, photos:[...f.photos, data.publicUrl]}))
      toast.success("Photo uploaded!")
    } catch(e) { toast.error(e.message) }
    finally { setUploadingPhoto(false) }
  }

  async function submitDealerApplication() {
    if(!dealerForm.showroom_name||!dealerForm.showroom_location||!dealerForm.showroom_phone) return toast.error("Please fill in all required fields")
    setSubmittingDealer(true)
    try {
      await supabase.from("dealer_applications").insert({
        user_id: user.id,
        showroom_name: dealerForm.showroom_name,
        showroom_location: dealerForm.showroom_location,
        showroom_phone: dealerForm.showroom_phone,
        showroom_email: dealerForm.showroom_email||null,
        brands_sold: dealerForm.brands_sold ? dealerForm.brands_sold.split(",").map(b=>b.trim()).filter(Boolean) : [],
        monthly_stock: dealerForm.monthly_stock ? Number(dealerForm.monthly_stock) : null,
        status: "pending"
      })
      const {data:admins} = await supabase.from("profiles").select("id").eq("role","admin")
      if(admins?.length>0) await supabase.from("notifications").insert(admins.map(a=>({
        user_id:a.id, type:"dealer_application", title:"New Dealer Application",
        message:dealerForm.showroom_name+" has applied to list cars on CCC."
      })))
      toast.success("Application submitted! We will contact you within 24 hours.")
      setDealerStatus("pending")
      setShowDealerForm(false)
    } catch(e) { toast.error(e.message) }
    finally { setSubmittingDealer(false) }
  }

  async function save(e) {
    e.preventDefault()
    if (!form.brand||!form.model||!form.price||!form.showroom_name||!form.showroom_location) {
      return toast.error("Brand, model, price and showroom details required")
    }
    setSaving(true)
    try {
      const payload = {
        dealer_id: user.id,
        brand: form.brand,
        model: form.model,
        variant: form.variant||null,
        year: Number(form.year),
        body_type: form.body_type,
        engine_cc: form.engine_cc?Number(form.engine_cc):null,
        transmission: form.transmission,
        fuel_type: form.fuel_type,
        drive_type: form.drive_type,
        color: form.color||null,
        interior_color: form.interior_color||null,
        seats: Number(form.seats)||5,
        doors: Number(form.doors)||4,
        price: Number(form.price),
        discount_price: form.discount_price?Number(form.discount_price):null,
        price_negotiable: form.price_negotiable,
        description: form.description||null,
        features: form.features ? form.features.split(",").map(f=>f.trim()).filter(Boolean) : [],
        photos: form.photos,
        video_url: form.video_url||null,
        showroom_name: form.showroom_name,
        showroom_location: form.showroom_location,
        showroom_phone: form.showroom_phone||null,
        showroom_email: form.showroom_email||null,
        stock_count: Number(form.stock_count)||1,
        listing_fee_amount: fees.listing_fee,
        updated_at: new Date().toISOString()
      }

      if (editing) {
        await supabase.from("new_car_listings").update(payload).eq("id",editing).eq("dealer_id",user.id)
        toast.success("Listing updated!")
      } else {
        await supabase.from("new_car_listings").insert(payload)
        toast.success("Listing created! Pay the listing fee to activate it.")
      }
      setShowForm(false); setEditing(null); setForm(EMPTY); load()
    } catch(e) { toast.error(e.message) }
    finally { setSaving(false) }
  }
  async function payListingFee(listing) {
    try {
      const { data, error } = await supabase.functions.invoke("daraja-stk-push", {
        body: {
          amount: fees.listing_fee,
          booking_id: listing.id,
          customer_id: user.id,
          phone: listing.showroom_phone||profile?.phone||"",
          service_name: `New car listing fee - ${listing.brand} ${listing.model}`
        }
      })
      if(error) throw error
      if(data?.success) toast.success("STK Push sent! Check your phone for M-Pesa prompt.")
      else toast.error("Payment initiation failed. Please try again.")
    } catch(e) { toast.error(e.message) }
  }
  async function payFeatureFee(listing, tier) {
    const tierAmounts = { day: fees.featured_day, week: fees.featured_week, month: fees.featured_month }
    const tierDays = { day: 1, week: 7, month: 30 }
    const amount = tierAmounts[tier]
    try {
      // Create the payment record FIRST and use ITS id as the payment reference, not the
      // listing's own id - the listing fee, feature fee, and lead fee all reference the same
      // listing otherwise, making it impossible for the payment callback to tell which one a
      // given confirmation is actually for. Also stopped marking is_featured true immediately
      // after merely sending the STK push - a customer could cancel or never enter their PIN
      // and still get the featured boost for free, since nothing had confirmed the money moved.
      const { data: payRecord, error: payRecErr } = await supabase.from("featured_payments").insert({
        listing_id: listing.id, seller_id: user.id, amount, weeks: tierDays[tier], status: "pending"
      }).select("id").single()
      if (payRecErr) throw payRecErr
      const { data, error } = await supabase.functions.invoke("daraja-stk-push", {
        body: {
          amount,
          booking_id: payRecord.id,
          customer_id: user.id,
          phone: listing.showroom_phone||profile?.phone||"",
          service_name: `Feature listing (${tier}) - ${listing.brand} ${listing.model}`
        }
      })
      if(error) throw error
      if(data?.success) toast.success("STK Push sent! Check your phone for M-Pesa prompt.")
      else toast.error("Payment initiation failed. Please try again.")
    } catch(e) { toast.error(e.message) }
  }

  async function payLeadFee(enquiry) {
    try {
      const { data, error } = await supabase.functions.invoke("daraja-stk-push", {
        body: {
          booking_id: enquiry.id,
          amount: fees.lead_fee,
          phone: profile?.phone || "",
          account_ref: enquiry.id.substring(0,12),
          description: `Lead fee - ${enquiry.customer_name}`
        }
      })
      if(error) throw error
      // No longer marking lead_fee_paid here - only the real payment callback should do
      // that, once Safaricom genuinely confirms the money moved, not just because a prompt was sent.
      if(data?.success) toast.success("STK Push sent! Check your phone 📱")
      else toast.error("Payment failed. Try again.")
    } catch(e) { toast.error(e.message) }
  }
  async function updateEnquiryStatus(id, status) {
    try {
      const { error } = await supabase.from("car_enquiries").update({ status }).eq("id", id).eq("dealer_id", user.id)
      if (error) throw error
      toast.success("Status updated to " + status)
      load()
    } catch (e) { toast.error(e.message) }
  }
  async function deleteListing(id) {
    if (!confirm("Delete this listing?")) return
    await supabase.from("new_car_listings").delete().eq("id",id).eq("dealer_id",user.id)
    toast.success("Listing deleted")
    load()
  }

  function startEdit(l) {
    setEditing(l.id)
    setForm({
      brand:l.brand, model:l.model, variant:l.variant||"", year:l.year, body_type:l.body_type,
      engine_cc:l.engine_cc||"", transmission:l.transmission, fuel_type:l.fuel_type, drive_type:l.drive_type,
      color:l.color||"", interior_color:l.interior_color||"", seats:l.seats||5, doors:l.doors||4,
      price:l.price, discount_price:l.discount_price||"", price_negotiable:l.price_negotiable||false,
      description:l.description||"", features:(l.features||[]).join(", "), photos:l.photos||[],
      showroom_name:l.showroom_name, showroom_location:l.showroom_location,
      showroom_phone:l.showroom_phone||"", showroom_email:l.showroom_email||"",
      stock_count:l.stock_count||1
    })
    setShowForm(true)
  }

  const inp = { width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"10px 12px", color:"#000", fontSize:12, outline:"none", fontFamily:"DM Sans,sans-serif", marginBottom:8 }
  const lbl = { fontSize:10, color:"#666", textTransform:"uppercase", display:"block", marginBottom:3 }
  const newEnquiries = enquiries.filter(e=>e.status==="new").length

  return (
    <div>
      <div style={{ fontFamily:"Syne", fontSize:isMobile?16:20, fontWeight:800, color:"#000", marginBottom:4 }}>My Car Listings</div>
      <div style={{ fontSize:12, color:"#777", marginBottom:"1.25rem" }}>Manage your new car listings and enquiries</div>
      {/* Dealer gate */}
      {!isDealer&&(
        <div style={{ background:"#fff8f0", border:"1px solid #e6821e30", borderRadius:12, padding:"1.5rem", marginBottom:"1.25rem", textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:8 }}>&#127962;</div>
          {dealerStatus==="pending"&&<div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:6 }}>Application Under Review</div>}
          {dealerStatus==="pending"&&<div style={{ fontSize:13, color:"#888" }}>Your dealer application is under review. We will notify you within 24 hours.</div>}
          {dealerStatus==="rejected"&&<div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#e24b4a", marginBottom:6 }}>Application Not Approved</div>}
          {dealerStatus==="rejected"&&<div style={{ fontSize:13, color:"#888" }}>Contact us at carcareconnect254@gmail.com for more information.</div>}
          {!dealerStatus&&!showDealerForm&&(
            <div>
              <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:6 }}>Become a CCC Dealer</div>
              <div style={{ fontSize:13, color:"#666", marginBottom:12 }}>Register your showroom to list vehicles on Car Care Connect.</div>
              <button onClick={()=>setShowDealerForm(true)}
                style={{ background:"#e6821e", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:"pointer" }}>
                Apply as Dealer
              </button>
            </div>
          )}
          {showDealerForm&&(
            <div style={{ textAlign:"left", marginTop:12 }}>
              <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:12 }}>Register as a Car Dealer</div>
              <input placeholder="Showroom name *" value={dealerForm.showroom_name} onChange={e=>setDealerForm(f=>({...f,showroom_name:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
              <input placeholder="Location *" value={dealerForm.showroom_location} onChange={e=>setDealerForm(f=>({...f,showroom_location:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
              <input placeholder="Phone number *" value={dealerForm.showroom_phone} onChange={e=>setDealerForm(f=>({...f,showroom_phone:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
              <input placeholder="Email" value={dealerForm.showroom_email} onChange={e=>setDealerForm(f=>({...f,showroom_email:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
              <input placeholder="Brands you sell e.g Toyota, Mazda" value={dealerForm.brands_sold} onChange={e=>setDealerForm(f=>({...f,brands_sold:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:8, boxSizing:"border-box" }}/>
              <input placeholder="Average monthly stock" value={dealerForm.monthly_stock} onChange={e=>setDealerForm(f=>({...f,monthly_stock:e.target.value}))}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", fontSize:13, marginBottom:12, boxSizing:"border-box" }}/>
              <div style={{ background:"#fff8f0", border:"1px solid #e6821e30", borderRadius:8, padding:"10px 12px", marginBottom:12, fontSize:12, color:"#666" }}>
                <div style={{ fontWeight:700, color:"#e6821e", marginBottom:4, display:"flex", alignItems:"center", gap:6 }}><PaymentsIcon size={14} color="#e6821e"/> Listing fee: KES {fees.listing_fee.toLocaleString()}/month</div>
                <div>After approval, you will pay KES {fees.listing_fee.toLocaleString()}/month to list unlimited cars. Our team will contact you within 24 hours to complete onboarding.</div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={submitDealerApplication} disabled={submittingDealer}
                  style={{ flex:1, background:"#e6821e", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, padding:"11px", cursor:"pointer" }}>
                  {submittingDealer?"Submitting...":"Submit Application"}
                </button>
                <button onClick={()=>setShowDealerForm(false)}
                  style={{ background:"#f5f5f5", border:"none", borderRadius:8, color:"#555", fontSize:13, padding:"11px 16px", cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:"1.25rem" }}>
        {[
          { label:"Total listings", value:listings.length, color:"#000" },
          { label:"Active", value:listings.filter(l=>l.is_active).length, color:"#1d9e75" },
          { label:"Pending payment", value:listings.filter(l=>!l.listing_fee_paid).length, color:"#e6821e" },
          { label:"New enquiries", value:newEnquiries, color:newEnquiries>0?"#e24b4a":"#555" },
        ].map(s=>(
          <div key={s.label} style={{ background:"#f8f8f8", borderRadius:10, padding:"0.75rem", border:"1px solid #eeeeee", textAlign:"center" }}>
            <div style={{ fontFamily:"Syne", fontSize:isMobile?14:18, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:9, color:"#888", marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:"1.25rem" }}>
        {[{k:"listings",l:"My Listings"},{k:"enquiries",l:`Enquiries (${enquiries.length})`}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            style={{ padding:"8px 16px", borderRadius:8, border:"none", fontSize:12, cursor:"pointer", background:tab===t.k?"#e6821e":"#f8f8f8", color:tab===t.k?"#fff":"#666", fontWeight:tab===t.k?700:400 }}>
            {t.l}
          </button>
        ))}
        <button onClick={()=>{ if(!isDealer){ setShowDealerForm(true); return } setShowForm(true); setEditing(null); setForm(EMPTY) }}
          style={{ marginLeft:"auto", background:"#e6821e", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:700, padding:"8px 16px", cursor:"pointer" }}>
          + Add listing
        </button>
      </div>

      {/* Add/Edit form */}
      {showForm&&(
        <div style={{ background:"#f8f8f8", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1.5rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000", marginBottom:"1rem" }}>{editing?"Edit listing":"Add new car listing"}</div>
          <form onSubmit={save}>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10, marginBottom:8 }}>
              <div><label style={lbl}>Brand *</label><input style={inp} placeholder="e.g. Toyota" value={form.brand} onChange={e=>setForm(f=>({...f,brand:e.target.value}))} required/></div>
              <div><label style={lbl}>Model *</label><input style={inp} placeholder="e.g. Fortuner" value={form.model} onChange={e=>setForm(f=>({...f,model:e.target.value}))} required/></div>
              <div><label style={lbl}>Variant</label><input style={inp} placeholder="e.g. 2.8L GD-6 4WD" value={form.variant} onChange={e=>setForm(f=>({...f,variant:e.target.value}))}/></div>
              <div><label style={lbl}>Year *</label><input style={inp} type="number" min="2000" max={new Date().getFullYear()+2} value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))} required/></div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:10, marginBottom:8 }}>
              <div><label style={lbl}>Body type</label>
                <select style={inp} value={form.body_type} onChange={e=>setForm(f=>({...f,body_type:e.target.value}))}>
                  {BODY_TYPES.map(b=><option key={b} value={b}>{b.charAt(0).toUpperCase()+b.slice(1)}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Transmission</label>
                <select style={inp} value={form.transmission} onChange={e=>setForm(f=>({...f,transmission:e.target.value}))}>
                  {TRANSMISSIONS.map(t=><option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Fuel type</label>
                <select style={inp} value={form.fuel_type} onChange={e=>setForm(f=>({...f,fuel_type:e.target.value}))}>
                  {FUEL_TYPES.map(f=><option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Drive type</label>
                <select style={inp} value={form.drive_type} onChange={e=>setForm(f=>({...f,drive_type:e.target.value}))}>
                  {DRIVE_TYPES.map(d=><option key={d} value={d}>{d.toUpperCase()}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Engine (cc)</label><input style={inp} type="number" placeholder="e.g. 2800" value={form.engine_cc} onChange={e=>setForm(f=>({...f,engine_cc:e.target.value}))}/></div>
              <div><label style={lbl}>Seats</label><input style={inp} type="number" min="2" max="15" value={form.seats} onChange={e=>setForm(f=>({...f,seats:e.target.value}))}/></div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10, marginBottom:8 }}>
              <div><label style={lbl}>Exterior color</label><input style={inp} placeholder="e.g. Pearl White" value={form.color} onChange={e=>setForm(f=>({...f,color:e.target.value}))}/></div>
              <div><label style={lbl}>Interior color</label><input style={inp} placeholder="e.g. Black leather" value={form.interior_color} onChange={e=>setForm(f=>({...f,interior_color:e.target.value}))}/></div>
              <div><label style={lbl}>Price (KES) *</label><input style={inp} type="number" placeholder="e.g. 4500000" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))} required/></div>
              <div><label style={lbl}>Discount price (KES)</label><input style={inp} type="number" placeholder="Leave blank if no discount" value={form.discount_price} onChange={e=>setForm(f=>({...f,discount_price:e.target.value}))}/></div>
              <div><label style={lbl}>Stock available</label><input style={inp} type="number" min="1" value={form.stock_count} onChange={e=>setForm(f=>({...f,stock_count:e.target.value}))}/></div>
              <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:20 }}>
                <input type="checkbox" id="negotiable" checked={form.price_negotiable} onChange={e=>setForm(f=>({...f,price_negotiable:e.target.checked}))}/>
                <label htmlFor="negotiable" style={{ fontSize:12, color:"#555" }}>Price negotiable</label>
              </div>
            </div>

            <div style={{ marginBottom:8 }}>
              <label style={lbl}>Features (comma separated)</label>
              <input style={inp} placeholder="e.g. Sunroof, Android Auto, Leather seats, Reverse camera" value={form.features} onChange={e=>setForm(f=>({...f,features:e.target.value}))}/>
            </div>
            <div style={{ marginBottom:8 }}>
              <label style={lbl}>Description</label>
              <textarea style={{...inp, resize:"vertical", minHeight:80}} placeholder="Describe the vehicle..." value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
            </div>

            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#000", marginBottom:8, marginTop:8 }}>Showroom Details</div>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10, marginBottom:8 }}>
              <div><label style={lbl}>Showroom name *</label><input style={inp} placeholder="e.g. Toyota Kenya Westlands" value={form.showroom_name} onChange={e=>setForm(f=>({...f,showroom_name:e.target.value}))} required/></div>
              <div><label style={lbl}>Location *</label><input style={inp} placeholder="e.g. Westlands, Nairobi" value={form.showroom_location} onChange={e=>setForm(f=>({...f,showroom_location:e.target.value}))} required/></div>
              <div><label style={lbl}>Phone</label><input style={inp} type="tel" placeholder="0712 345 678" value={form.showroom_phone} onChange={e=>setForm(f=>({...f,showroom_phone:e.target.value}))}/></div>
              <div><label style={lbl}>Email</label><input style={inp} type="email" placeholder="showroom@dealer.co.ke" value={form.showroom_email} onChange={e=>setForm(f=>({...f,showroom_email:e.target.value}))}/></div>
            </div>

            {/* Photos */}
            <div style={{ marginBottom:"1rem" }}>
              <label style={lbl}>Photos ({form.photos.length} uploaded)</label>
            {/* Video upload */}
            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Video walkthrough (optional)</label>
              {form.video_url&&(
                <div style={{ position:"relative", marginBottom:8 }}>
                  <video src={form.video_url} controls style={{ width:"100%", maxHeight:200, borderRadius:10 }}/>
                  <button type="button" onClick={()=>setForm(f=>({...f,video_url:""}))}
                    style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.5)", border:"none", borderRadius:"50%", width:24, height:24, color:"#fff", fontSize:12, cursor:"pointer" }}>×</button>
                </div>
              )}
              <label style={{ display:"block", width:"100%", background:"#f8f8f8", border:"2px dashed #e5e5e5", borderRadius:10, padding:"12px", color:"#888", fontSize:12, cursor:"pointer", textAlign:"center" }}>
                {uploadingVideo?`⏳ Uploading... ${videoUploadProgress}%`:"🎥 "+(form.video_url?"Change video":"Upload video walkthrough (max 200MB)")}
                <input type="file" accept="video/*" style={{ display:"none" }} onChange={async e=>{
                  const file = e.target.files[0]
                  if (!file) return
                  if (file.size > 200*1024*1024) return toast.error("Video must be under 200MB")
                  setUploadingVideo(true)
                  setVideoUploadProgress(0)
                  try {
                    const ext = file.name.split(".").pop()
                    const path = user.id+"/car-video-"+Date.now()+"."+ext
                    const { data: { session } } = await supabase.auth.getSession()
                    await new Promise((resolve, reject) => {
                      const xhr = new XMLHttpRequest()
                      xhr.open("POST", `${import.meta.env.VITE_SUPABASE_URL || "https://gcnefnqtjxtqbhynyoxe.supabase.co"}/storage/v1/object/marketplace/${path}`)
                      xhr.setRequestHeader("Authorization", `Bearer ${session?.access_token}`)
                      xhr.setRequestHeader("x-upsert", "true")
                      xhr.upload.onprogress = (evt) => {
                        if (evt.lengthComputable) setVideoUploadProgress(Math.round((evt.loaded / evt.total) * 100))
                      }
                      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed: " + xhr.responseText))
                      xhr.onerror = () => reject(new Error("Network error during upload"))
                      xhr.send(file)
                    })
                    const { data } = supabase.storage.from("marketplace").getPublicUrl(path)
                    setForm(f=>({...f,video_url:data.publicUrl}))
                    toast.success("Video uploaded!")
                  } catch(e) { toast.error(e.message) }
                  finally { setUploadingVideo(false); setVideoUploadProgress(0) }
                }}/>
              </label>
            </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
                {form.photos.map((p,i)=>(
                  <div key={i} style={{ position:"relative" }}>
                    <img src={p} alt="" style={{ width:80, height:60, objectFit:"cover", borderRadius:6 }}/>
                    <button type="button" onClick={()=>setForm(f=>({...f,photos:f.photos.filter((_,j)=>j!==i)}))}
                      style={{ position:"absolute", top:-6, right:-6, background:"#e24b4a", border:"none", borderRadius:"50%", width:18, height:18, color:"#fff", fontSize:10, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                  </div>
                ))}
                <label style={{ width:80, height:60, background:"#f0f0f0", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", border:"1px dashed #ccc", fontSize:20, color:"#888" }}>
                  {uploadingPhoto?"⏳":"+"}
                  <input type="file" accept="image/*" style={{ display:"none" }} ref={photoRef} onChange={e=>e.target.files[0]&&uploadPhoto(e.target.files[0])} disabled={uploadingPhoto}/>
                </label>
              </div>
            </div>

            <div style={{ background:"#fff8f0", border:"1px solid #e6821e30", borderRadius:8, padding:"0.75rem", marginBottom:"1rem" }}>
              <div style={{ fontSize:11, color:"#e6821e", fontWeight:600, marginBottom:4 }}>💳 Listing fee: KES {fees.listing_fee.toLocaleString()}/month</div>
              <div style={{ fontSize:11, color:"#555" }}>After saving, pay the listing fee to activate your listing for {fees.duration_days} days. Admin will review and approve within 24 hours.</div>
            </div>

            <div style={{ display:"flex", gap:8 }}>
              <button type="submit" disabled={saving} style={{ background:saving?"#ccc":"#e6821e", border:"none", borderRadius:9, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:saving?"not-allowed":"pointer" }}>
                {saving?"Saving...":editing?"Update listing":"Save listing"}
              </button>
              <button type="button" onClick={()=>{ setShowForm(false); setEditing(null); setForm(EMPTY) }} style={{ background:"none", border:"1px solid #ddd", borderRadius:9, color:"#666", fontSize:12, padding:"11px 16px", cursor:"pointer" }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Listings tab */}
      {tab==="listings"&&(
        <div>
          {loading&&<div style={{ color:"#888", fontSize:13 }}>Loading...</div>}
          {!loading&&listings.length===0&&(
            <div style={{ textAlign:"center", padding:"3rem", color:"#888" }}>
              <div style={{ marginBottom:12, display:"flex", justifyContent:"center" }}><VehicleIcon size={40} color="#e6821e"/></div>
              <div>No listings yet. Add your first car listing above.</div>
            </div>
          )}
          {listings.map(l=>(
            <div key={l.id} style={{ background:"#ffffff", border:`1px solid ${l.is_active?"#1d9e7530":l.listing_fee_paid?"#e6821e30":"#eeeeee"}`, borderRadius:12, padding:"1rem", marginBottom:10 }}>
              <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                {l.photos?.[0]&&<img src={l.photos[0]} alt="" style={{ width:80, height:60, objectFit:"cover", borderRadius:8, flexShrink:0 }}/>}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:"#000" }}>{l.year} {l.brand} {l.model}</div>
                      <div style={{ fontSize:11, color:"#888" }}>{l.showroom_name} · {l.showroom_location}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:800, color:"#e6821e" }}>KES {Number(l.price).toLocaleString()}</div>
                      <span style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:l.is_active?"#f0fdf4":l.listing_fee_paid?"#fff8f0":"#f5f5f5", color:l.is_active?"#1d9e75":l.listing_fee_paid?"#e6821e":"#888" }}>
                        {l.is_active?`✅ Active until ${l.listing_expires_at?new Date(l.listing_expires_at).toLocaleDateString():"—"}`:l.listing_fee_paid?"⏳ Pending approval":"💳 Fee required"}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:"#aaa", marginBottom:8 }}>👁 {l.views||0} views · 💬 {l.enquiries||0} enquiries · Expires: {l.listing_expires_at?new Date(l.listing_expires_at).toLocaleDateString():"—"}</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {!l.listing_fee_paid&&(
                      <button onClick={()=>payListingFee(l)} style={{ background:"#e6821e", border:"none", borderRadius:7, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:11, fontWeight:700, padding:"5px 12px", cursor:"pointer" }}>
                        <><PaymentsIcon size={13} color="currentColor"/> Pay KES {fees.listing_fee.toLocaleString()} to activate</>
                      </button>
                    )}
                    {l.is_active&&(!l.is_featured||new Date(l.featured_until)<new Date())&&(
                      <>
                        <button onClick={()=>payFeatureFee(l,"day")} style={{ background:"#faf5ff", border:"1px solid #8b5cf640", borderRadius:7, color:"#8b5cf6", fontSize:11, fontWeight:700, padding:"5px 12px", cursor:"pointer" }}>
                          Feature 1 day - KES {fees.featured_day.toLocaleString()}
                        </button>
                        <button onClick={()=>payFeatureFee(l,"week")} style={{ background:"#faf5ff", border:"1px solid #8b5cf640", borderRadius:7, color:"#8b5cf6", fontSize:11, fontWeight:700, padding:"5px 12px", cursor:"pointer" }}>
                          Feature 1 week - KES {fees.featured_week.toLocaleString()}
                        </button>
                        <button onClick={()=>payFeatureFee(l,"month")} style={{ background:"#faf5ff", border:"1px solid #8b5cf640", borderRadius:7, color:"#8b5cf6", fontSize:11, fontWeight:700, padding:"5px 12px", cursor:"pointer" }}>
                          Feature 1 month - KES {fees.featured_month.toLocaleString()}
                        </button>
                      </>
                    )}
                    <button onClick={()=>startEdit(l)} style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>Edit</button>
                    <button onClick={()=>deleteListing(l.id)} style={{ background:"none", border:"1px solid #e24b4a30", borderRadius:7, color:"#e24b4a", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>Delete</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enquiries tab */}
      {tab==="enquiries"&&(
        <div>
          {enquiries.length===0&&<div style={{ color:"#888", fontSize:13, textAlign:"center", padding:"2rem" }}>No enquiries yet</div>}
          {enquiries.map(e=>(
            <div key={e.id} style={{ background:"#f8f8f8", border:`1px solid ${e.status==="new"?"#e24b4a20":"#eeeeee"}`, borderRadius:10, padding:"1rem", marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:"#000" }}>{e.customer_name}</div>
                  {e.lead_fee_paid ? (
                  <div style={{ fontSize:11, color:"#888" }}>📞 {e.customer_phone} {e.customer_email?`· ✉️ ${e.customer_email}`:""}</div>
                ) : (
                  <div style={{ fontSize:11, color:"#aaa", fontStyle:"italic" }}>🔒 Contact details hidden until lead fee is paid</div>
                )}
                  <div style={{ fontSize:11, color:"#888", display:"flex", alignItems:"center", gap:3 }}><VehicleIcon size={11} color="#888"/> {e.new_car_listings?.year} {e.new_car_listings?.brand} {e.new_car_listings?.model}</div>
                  <div style={{ fontSize:10, color:"#888" }}>{e.enquiry_type?.replace(/_/g," ")} · Prefers: {e.preferred_contact}</div>
                  {e.message&&<div style={{ fontSize:11, color:"#555", marginTop:4, fontStyle:"italic" }}>&quot;{e.message}&quot;</div>}
                  <div style={{ fontSize:10, color:"#aaa", marginTop:4 }}>{new Date(e.created_at).toLocaleString()}</div>
                </div>
                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:e.status==="new"?"#fff5f5":e.status==="contacted"?"#eff6ff":e.status==="converted"?"#f0fdf4":"#f5f5f5", color:e.status==="new"?"#e24b4a":e.status==="contacted"?"#378add":e.status==="converted"?"#1d9e75":"#888", fontWeight:600 }}>
                  {e.status}
                </span>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {e.status==="new"&&<button onClick={()=>updateEnquiryStatus(e.id,"contacted")} style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"4px 10px", cursor:"pointer" }}>✓ Mark contacted</button>}
                {e.status==="contacted"&&<button onClick={()=>updateEnquiryStatus(e.id,"converted")} style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"4px 10px", cursor:"pointer" }}>🎉 Mark sold</button>}
                {!e.lead_fee_paid&&<button onClick={()=>payLeadFee(e)} style={{ background:"#faf5ff", border:"1px solid #8b5cf640", borderRadius:7, color:"#8b5cf6", fontSize:11, fontWeight:700, padding:"4px 10px", cursor:"pointer" }}>🔓 Pay KES {fees.lead_fee.toLocaleString()} to unlock contact</button>}
                {e.lead_fee_paid&&<span style={{ fontSize:10, color:"#1d9e75", padding:"4px 8px" }}>✓ Lead fee paid</span>}
                {e.lead_fee_paid&&e.customer_phone&&<a href={`tel:${e.customer_phone}`} style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"4px 10px", textDecoration:"none" }}>📞 Call</a>}
                {e.lead_fee_paid&&e.customer_phone&&<a href={`https://wa.me/254${e.customer_phone.replace(/^0/,"")}`} target="_blank" rel="noreferrer" style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"4px 10px", textDecoration:"none" }}>💚 WhatsApp</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}



